import React from 'react';
import { PageWrapper } from '@components/layout/page-wrapper';
import { useMediaAsset } from '@hooks/use-media-asset';
import { getBlogImageName } from '@config/media-mapping';
import {
  BlogContainer,
  HeroSection,
  Tag,
  Title,
  Subtitle,
  Byline,
  ArticleBody,
  ChartFigure,
  ChartCaption,
  Blockquote,
  StatGrid,
  StatCard,
  StatNumber,
  StatLabel,
  Timeline,
  TimelineItem,
  TimelineDate,
  TimelineText,
  Divider,
} from './ai-uneven-impact-blog-page.styles';

export const AIUnevenImpactBlogPage: React.FC = () => {
  const assetName = getBlogImageName('ai-uneven-impact');
  const { url: chartUrl } = useMediaAsset(assetName);

  return (
    <PageWrapper>
      <BlogContainer>
        <HeroSection>
          <Tag>Analysis &middot; February 2026</Tag>
          <Title>
            AI&rsquo;s Uneven Impact
          </Title>
          <Subtitle>
            AI makes prototypes fly and high precision code cry.
            Here&rsquo;s what 2025 taught us &mdash; and why the gap is about to
            get a whole lot wider.
          </Subtitle>
          <Byline>
            Hypothesis By <span>June</span> &middot; 6 min read
          </Byline>
          <Byline>
            Writing and Research By <span>Claude</span>
          </Byline>
        </HeroSection>

        <ArticleBody>
          <p>
            There&rsquo;s a chart I keep coming back to. It shows a simple curve
            &mdash; AI&rsquo;s impact as a function of code maturity &mdash; and
            every quarter, the curve gets more extreme. The left side (quick
            prototypes, MVPs, weekend projects) rockets upward. The right side
            (production systems chasing five-nines uptime) plunges further into
            the negative. Same technology. Radically different outcomes.
          </p>

          <ChartFigure>
            {chartUrl && (
              <img
                src={chartUrl}
                alt="Animated chart showing AI's uneven impact across code maturity levels from Q1 2025 to Q1 2028"
              />
            )}
            <ChartCaption>
              AI&rsquo;s impact diverges dramatically by code maturity level.
              Low-maturity code sees exponential gains while battle-tested
              production systems face mounting costs. Animation spans Q1 2025
              &ndash; Q1 2028.
            </ChartCaption>
          </ChartFigure>

          <p>
            The punchline isn&rsquo;t that AI helps with easy stuff and struggles
            with hard stuff. That&rsquo;s obvious. The punchline is that{' '}
            <strong>the gap is accelerating</strong> &mdash; and we have about
            eighteen months of data proving it.
          </p>

          <h2>The Receipts: 2025 in Numbers</h2>

          <StatGrid>
            <StatCard>
              <StatNumber $color="green">85%</StatNumber>
              <StatLabel>
                of developers now use AI coding tools regularly
              </StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber $color="coral">8&times;</StatNumber>
              <StatLabel>
                increase in duplicated code blocks since 2022
              </StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber $color="lavender">41%</StatNumber>
              <StatLabel>
                of all code written in 2025 was AI-generated
              </StatLabel>
            </StatCard>
          </StatGrid>

          <p>
            Here&rsquo;s the paradox that defined 2025: developers <em>felt</em>{' '}
            20% faster with AI, but a rigorous randomized controlled trial from
            METR found they were actually <strong>19% slower</strong> on mature
            open-source projects. Experienced developers working on codebases
            they&rsquo;d maintained for years &mdash; their home turf &mdash;
            were measurably less productive when using AI tools.
          </p>

          <p>
            Meanwhile, GitClear analyzed 211 million changed lines of code
            across repos owned by Google, Microsoft, and Meta. Their findings?
            Code duplication is up eightfold since 2022. Copy-pasted code now
            exceeds refactored code for the first time in the history of their
            tracking. Code churn &mdash; code added then discarded within two
            weeks &mdash; nearly doubled from 2020 to 2024, projected at 7.9% of
            all new code.
          </p>

          <Blockquote>
            &ldquo;I don&rsquo;t think I have ever seen so much technical debt
            being created in such a short period of time during my 35-year
            career in technology.&rdquo;
            <cite>&mdash; Kin Lane, API Evangelist</cite>
          </Blockquote>

          <p>
            And yet &mdash; and this is the part that makes the chart work
            &mdash;{' '}
            <strong>
              the same tools are absurdly good for greenfield work.
            </strong>{' '}
            GitHub Copilot users complete 126% more projects per week. Developers
            report saving 30&ndash;60% of time on coding, testing, and
            documentation. Non-developers are building real products from
            scratch: one user grew an app to $7,000 MRR using Claude as their
            sole engineer. The Stack Overflow 2025 survey found 84% adoption, up
            from 76% the prior year.
          </p>

          <p>
            AI isn&rsquo;t uniformly good or bad. It&rsquo;s a <em>wedge</em>,
            and the wedge is widening.
          </p>

          <h3>What shipped in 2025</h3>

          <Timeline>
            <TimelineItem>
              <TimelineDate>Feb 2025</TimelineDate>
              <TimelineText>
                <strong>Claude Code</strong> launches as research preview &mdash;
                terminal-native, agentic coding arrives
              </TimelineText>
            </TimelineItem>
            <TimelineItem>
              <TimelineDate>May 2025</TimelineDate>
              <TimelineText>
                <strong>Claude Code GA</strong> &mdash; less than a year from
                research preview to production tool
              </TimelineText>
            </TimelineItem>
            <TimelineItem>
              <TimelineDate>Jun 2025</TimelineDate>
              <TimelineText>
                <strong>Cursor 2.0</strong> switches to credit-based pricing,
                ships parallel agent execution (8 simultaneous agents)
              </TimelineText>
            </TimelineItem>
            <TimelineItem>
              <TimelineDate>Mid 2025</TimelineDate>
              <TimelineText>
                <strong>SWE-bench scores</strong> jump from 4.4% (2023) to 69.1%
                &mdash; a 15&times; improvement in two years
              </TimelineText>
            </TimelineItem>
            <TimelineItem>
              <TimelineDate>Late 2025</TimelineDate>
              <TimelineText>
                <strong>OpenAI acquires Windsurf</strong>, Google ships Gemini 3
                Flash at 78% SWE-bench
              </TimelineText>
            </TimelineItem>
            <TimelineItem>
              <TimelineDate>Jan 2026</TimelineDate>
              <TimelineText>
                <strong>Claude Code 2.1</strong> &mdash; session teleportation,
                multi-agent orchestration, Chrome integration
              </TimelineText>
            </TimelineItem>
            <TimelineItem>
              <TimelineDate>Feb 2026</TimelineDate>
              <TimelineText>
                <strong>Opus 4.6</strong> ships at 80.9% SWE-bench &mdash; 1M
                token context holds entire large codebases in memory
              </TimelineText>
            </TimelineItem>
          </Timeline>

          <p>
            That&rsquo;s a dizzying pace. We went from &ldquo;AI can
            autocomplete a line&rdquo; to &ldquo;AI can take a vague
            instruction, work for an hour, and come back with tested, committed
            code&rdquo; in roughly two years. The agentic era is not a prediction
            anymore. It&rsquo;s a billing line item.
          </p>

          <Divider />

          <h2>Why the Curve Keeps Bending</h2>

          <p>
            The chart&rsquo;s shape isn&rsquo;t random. It&rsquo;s a consequence
            of a fundamental asymmetry in what AI does well versus what
            production code demands.
          </p>

          <p>
            <strong>On the left side of the curve</strong> &mdash; low-maturity,
            greenfield code &mdash; every model improvement directly translates
            to impact. A better model means better first drafts, fewer
            iterations, faster prototypes. Context windows expanding from 32K to
            1M tokens means the AI can hold your whole project in mind at once.
            For an MVP, that&rsquo;s everything. The marginal return on each
            capability jump is enormous, because the bottleneck was always
            &ldquo;getting something working&rdquo; &mdash; and AI has gotten
            terrifyingly good at that.
          </p>

          <p>
            <strong>On the right side</strong> &mdash; the Battle of the 9&rsquo;s
            &mdash; the bottleneck isn&rsquo;t writing code. It&rsquo;s
            understanding why a system behaves the way it does at 3 AM under
            50&times; normal load with a bad certificate and a race condition in
            the payment flow. It&rsquo;s the 99.99% uptime that took three years
            to earn. AI can&rsquo;t reason about the organizational context, the
            regulatory constraints, the war stories embedded in comment blocks
            that say <code>{'// DO NOT TOUCH — see incident #4471'}</code>.
          </p>

          <p>
            Worse, AI actively erodes the foundations that high-reliability
            systems are built on. Google&rsquo;s 2024 DORA report found a direct
            tradeoff: a 25% increase in AI usage produced a 7.2% decrease in
            delivery stability. Apiiro documented a{' '}
            <strong>
              10&times; increase in security vulnerabilities per month
            </strong>{' '}
            across Fortune 50 enterprises between December 2024 and June 2025.
            The Faros AI Productivity Paradox report, analyzing telemetry from
            10,000+ developers, concluded bluntly: developers write more code,
            but organizations see no measurable improvement in delivery outcomes.
          </p>

          <Blockquote>
            AI is like a &ldquo;brand new credit card here that is going to allow
            us to accumulate technical debt in ways we were never able to do
            before.&rdquo;
            <cite>&mdash; Armando Solar-Lezama, MIT</cite>
          </Blockquote>

          <Divider />

          <h2>2027&ndash;2028: Speculation, Responsibly</h2>

          <p>
            If the curve keeps bending &mdash; and there&rsquo;s no structural
            reason it won&rsquo;t &mdash; here&rsquo;s what the next two years
            look like:
          </p>

          <p>
            <strong>The left side goes off the chart.</strong> We&rsquo;re
            approaching a world where a solo founder with a laptop and an API key
            can build in a weekend what took a seed-stage team three months in
            2023. SWE-bench scores that double annually, context windows measured
            in millions of tokens, and multi-agent orchestration (Cursor already
            runs 8 agents in parallel) mean the time-to-prototype is collapsing
            toward zero. The 1000&times; multiplier on the chart isn&rsquo;t
            hyperbole &mdash; it&rsquo;s what happens when you compound
            2&ndash;3&times; annual improvements over three years.
          </p>

          <p>
            <strong>The right side gets worse before it gets better.</strong>{' '}
            Every AI-generated MVP that succeeds becomes a production system
            someone has to maintain. That eightfold increase in code duplication?
            It&rsquo;s compounding. The junior developers who would have learned
            to maintain these systems are being replaced &mdash; a 2025 LeadDev
            survey found 54% of engineering leaders plan to hire fewer juniors
            due to &ldquo;AI efficiencies.&rdquo; We&rsquo;re creating technical
            debt exponentially while simultaneously eliminating the people
            who&rsquo;d pay it down.
          </p>

          <p>
            <strong>The zero-crossing moves right.</strong> The point on the curve
            where AI&rsquo;s impact flips from positive to negative will shift as
            AI gets better at handling larger codebases. But it won&rsquo;t
            disappear. Systems at the 99.99% reliability tier will remain
            stubbornly resistant to AI acceleration for the same reason
            they&rsquo;re resistant to everything else: the last 0.01% is a
            fundamentally different problem than the first 99%.
          </p>

          <Divider />

          <h2>So What Do You Actually Do?</h2>

          <p>
            If you&rsquo;re building something new &mdash; lean into it. Hard.
            The upside is real and growing. Use AI tools aggressively for
            prototyping, MVPs, and early iterations. The developers shipping
            3&times; faster aren&rsquo;t imagining things; they&rsquo;re just
            working on the right part of the curve.
          </p>

          <p>
            If you&rsquo;re maintaining something that matters &mdash; be
            deliberate. Mandate human review. Invest in refactoring AI-generated
            code before it ships. Watch your churn metrics like a hawk. And for
            the love of your on-call rotation, don&rsquo;t let an AI agent push
            to production without a human who understands the blast radius.
          </p>

          <p>
            The uneven impact isn&rsquo;t a bug. It&rsquo;s the defining feature
            of how AI will reshape software for the next decade. The teams that
            win will be the ones who understand which side of the curve
            they&rsquo;re on &mdash; and act accordingly.
          </p>
        </ArticleBody>
      </BlogContainer>
    </PageWrapper>
  );
};
